import type { CSSProperties } from "react";

import { builtInLayoutPresetStore } from "./built-in-layout-presets";

export const layoutPresetStorageKey = "skygarden:layout-presets:v6";
export const layoutPresetChangeEvent = "skygarden:layout-presets-changed";
export const layoutPreviewMessageType = "skygarden:layout-preview-update";

export const layoutGroupIds = [
  "logo",
  "headerDownloads",
  "heading",
  "openingPack",
  "packs",
  "rewards",
  "garden",
  "tagline",
  "storeButtons",
  "footer",
] as const;

export type LayoutGroupId = (typeof layoutGroupIds)[number];
export type LayoutScene = "packet" | "packs" | "garden";

export type LayoutTransform = {
  x: number;
  y: number;
  scale: number;
};

export type LayoutSettings = Record<LayoutGroupId, LayoutTransform>;

export type LayoutPreset = {
  id: string;
  name: string;
  width: number;
  height: number;
  layout: LayoutSettings;
};

export type LayoutPresetStore = {
  version: 1;
  presets: LayoutPreset[];
};

export type LayoutPreviewMessage = {
  type: typeof layoutPreviewMessageType;
  layout: LayoutSettings;
};

export type LayoutCssVariables = CSSProperties & Record<`--layout-${string}`, string>;

const identityTransform: LayoutTransform = {
  x: 0,
  y: 0,
  scale: 1,
};

export const defaultLayoutSettings = Object.fromEntries(
  layoutGroupIds.map((groupId) => [groupId, { ...identityTransform }]),
) as LayoutSettings;

export const emptyLayoutPresetStore: LayoutPresetStore = {
  version: 1,
  presets: [],
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function finiteNumber(value: unknown, fallback: number) {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function normalizeTransform(value: unknown): LayoutTransform {
  if (!isRecord(value)) {
    return { ...identityTransform };
  }

  return {
    x: finiteNumber(value.x, identityTransform.x),
    y: finiteNumber(value.y, identityTransform.y),
    scale: Math.max(0.1, finiteNumber(value.scale, identityTransform.scale)),
  };
}

export function normalizeLayoutSettings(value: unknown): LayoutSettings {
  const source = isRecord(value) ? value : {};

  return Object.fromEntries(
    layoutGroupIds.map((groupId) => [groupId, normalizeTransform(source[groupId])]),
  ) as LayoutSettings;
}

export function cloneLayoutSettings(layout: LayoutSettings): LayoutSettings {
  return normalizeLayoutSettings(layout);
}

function normalizePreset(value: unknown): LayoutPreset | undefined {
  if (!isRecord(value) || typeof value.id !== "string") {
    return undefined;
  }

  const width = Math.round(finiteNumber(value.width, 0));
  const height = Math.round(finiteNumber(value.height, 0));

  if (width < 1 || height < 1) {
    return undefined;
  }

  return {
    id: value.id,
    name:
      typeof value.name === "string" && value.name.trim()
        ? value.name.trim()
        : `${width} × ${height}`,
    width,
    height,
    layout: normalizeLayoutSettings(value.layout),
  };
}

export function readLayoutPresetStore(): LayoutPresetStore {
  if (typeof window === "undefined") {
    return builtInLayoutPresetStore;
  }

  try {
    const rawValue = window.localStorage.getItem(layoutPresetStorageKey);

    if (!rawValue) {
      return builtInLayoutPresetStore;
    }

    const parsedValue: unknown = JSON.parse(rawValue);

    if (!isRecord(parsedValue) || !Array.isArray(parsedValue.presets)) {
      return builtInLayoutPresetStore;
    }

    const presets = parsedValue.presets
      .map(normalizePreset)
      .filter((preset): preset is LayoutPreset => Boolean(preset));

    return mergeLayoutPresetStores(builtInLayoutPresetStore, { version: 1, presets });
  } catch {
    return builtInLayoutPresetStore;
  }
}

export function writeLayoutPresetStore(store: LayoutPresetStore) {
  window.localStorage.setItem(layoutPresetStorageKey, JSON.stringify(store));
  window.dispatchEvent(new Event(layoutPresetChangeEvent));
}

export function findNearestLayoutPreset(
  store: LayoutPresetStore,
  width: number,
  height: number,
): LayoutPreset | undefined {
  const isPortrait = height >= width;
  const compatiblePresets = store.presets.filter(
    (preset) => preset.height >= preset.width === isPortrait,
  );
  let closestPreset: LayoutPreset | undefined;
  let closestScore = Number.POSITIVE_INFINITY;

  for (const preset of compatiblePresets) {
    const widthDelta = (preset.width - width) / Math.max(width, 1);
    const heightDelta = (preset.height - height) / Math.max(height, 1);
    const score = widthDelta ** 2 * 3 + heightDelta ** 2;

    if (score < closestScore) {
      closestPreset = preset;
      closestScore = score;
    }
  }

  return closestPreset;
}

type LayoutFamily = "landscape" | "portrait";

function getLayoutFamily(width: number, height: number): LayoutFamily {
  return height >= width ? "portrait" : "landscape";
}

function getViewportMetric(width: number, height: number) {
  return Math.hypot(width, height);
}

function lerp(start: number, end: number, progress: number) {
  return start + (end - start) * progress;
}

function interpolateLayouts(
  lowerLayout: LayoutSettings,
  upperLayout: LayoutSettings,
  progress: number,
): LayoutSettings {
  return Object.fromEntries(
    layoutGroupIds.map((groupId) => {
      const lower = lowerLayout[groupId];
      const upper = upperLayout[groupId];

      return [
        groupId,
        {
          x: lerp(lower.x, upper.x, progress),
          y: lerp(lower.y, upper.y, progress),
          scale: lerp(lower.scale, upper.scale, progress),
        },
      ];
    }),
  ) as LayoutSettings;
}

export function resolveLayoutForViewport(
  store: LayoutPresetStore,
  width: number,
  height: number,
): LayoutSettings {
  const exactPreset = store.presets.find(
    (preset) => preset.width === Math.round(width) && preset.height === Math.round(height),
  );

  if (exactPreset) {
    return cloneLayoutSettings(exactPreset.layout);
  }

  const family = getLayoutFamily(width, height);
  const compatiblePresets = store.presets
    .filter((preset) => getLayoutFamily(preset.width, preset.height) === family)
    .sort(
      (left, right) =>
        getViewportMetric(left.width, left.height) - getViewportMetric(right.width, right.height),
    );

  if (compatiblePresets.length === 0) {
    return cloneLayoutSettings(defaultLayoutSettings);
  }

  const viewportMetric = getViewportMetric(width, height);
  const firstPreset = compatiblePresets[0];
  const lastPreset = compatiblePresets.at(-1) ?? firstPreset;
  const firstMetric = getViewportMetric(firstPreset.width, firstPreset.height);
  const lastMetric = getViewportMetric(lastPreset.width, lastPreset.height);

  if (viewportMetric <= firstMetric) {
    return cloneLayoutSettings(firstPreset.layout);
  }

  if (viewportMetric >= lastMetric) {
    return cloneLayoutSettings(lastPreset.layout);
  }

  for (let index = 1; index < compatiblePresets.length; index += 1) {
    const upperPreset = compatiblePresets[index];
    const upperMetric = getViewportMetric(upperPreset.width, upperPreset.height);

    if (viewportMetric > upperMetric) {
      continue;
    }

    const lowerPreset = compatiblePresets[index - 1];
    const lowerMetric = getViewportMetric(lowerPreset.width, lowerPreset.height);
    const progress = (viewportMetric - lowerMetric) / Math.max(upperMetric - lowerMetric, 1);

    return interpolateLayouts(lowerPreset.layout, upperPreset.layout, progress);
  }

  return cloneLayoutSettings(lastPreset.layout);
}

function mergeLayoutPresetStores(
  baseStore: LayoutPresetStore,
  overrideStore: LayoutPresetStore,
): LayoutPresetStore {
  const presetsByViewport = new Map(
    baseStore.presets.map((preset) => [`${preset.width}x${preset.height}`, preset]),
  );

  for (const preset of overrideStore.presets) {
    presetsByViewport.set(`${preset.width}x${preset.height}`, preset);
  }

  return {
    version: 1,
    presets: [...presetsByViewport.values()],
  };
}

function groupCssName(groupId: LayoutGroupId) {
  return groupId.replace(/[A-Z]/g, (character) => `-${character.toLowerCase()}`);
}

export function getLayoutCssVariables(layout: LayoutSettings): LayoutCssVariables {
  const variables: LayoutCssVariables = {};

  for (const groupId of layoutGroupIds) {
    const cssName = groupCssName(groupId);
    const transform = layout[groupId];
    variables[`--layout-${cssName}-x`] = `${transform.x}px`;
    variables[`--layout-${cssName}-y`] = `${transform.y}px`;
    variables[`--layout-${cssName}-scale`] = `${transform.scale}`;
  }

  const logo = layout.logo;
  variables["--layout-logo-left-edge-x"] = `calc(${logo.x}px + ${(1 - logo.scale) * 5.240625}cqw)`;

  return variables;
}

export function isLayoutPreviewMessage(value: unknown): value is LayoutPreviewMessage {
  return isRecord(value) && value.type === layoutPreviewMessageType && isRecord(value.layout);
}
